<!--
This components makes heavy use of  VeeValidate (https://baianat.github.io/vee-validate/) for form validation
-->
<template>
  <form>

    <b-field grouped class="columns"  >
      <b-field label="Company Name" class="column" :type="calculateFieldType('company name')">
        <b-input v-model="companyInfo.companyName"
                 v-validate="{required: true}"
                 name="company name"
                 required
                 autofocus></b-input>
      </b-field>

      <b-field label="Industry" class="column" :type="calculateFieldType('industry')">
        <b-autocomplete :open-on-focus="true" :data="filteredIndustryList"
                        v-model="companyInfo.companyIndustry"
                        name="industry"
                        v-validate="{required: true}"></b-autocomplete>
      </b-field>

      <b-field label="Phone Number" class="column" :type="calculateFieldType('phone number')">
        <phone-number-input v-model="companyInfo.companyPhones[0].companyPhoneDigits"
                 v-validate="{required: true}"
                 name="phone number"
                 required
                 autofocus></phone-number-input>
      </b-field>
    </b-field>


    <b-field label="Street" :type="calculateFieldType('street address')">
      <b-input v-model="companyInfo.companyAddresses[0].companyAddressStreet1"
               name="street address"
               v-validate="{required: true}"
               required></b-input>
    </b-field>
<!-- TODO: replace this with the "AddressLookup" component -->
    <b-field grouped class="columns">
      <b-field label="Zip Code" class="column is-2" :type="calculateFieldType('zip code')">
        <b-input v-model="companyInfo.companyAddresses[0].companyAddressZip"
                 name="zip code"
                 v-validate="{required: true}"
                 required
                 @input="lookupAddress"
                 :loading="isFetchingAddress"></b-input>
      </b-field>

      <b-field label="State" class="column" :type="calculateFieldType('state')">
        <b-select v-model="companyInfo.companyAddresses[0].companyAddressState"
                  name="state"
                  v-validate="{required: true}"
                  required
                  expanded
                  :disabled="isFetchingAddress"
                  :loading="isFetchingAddress">
          <option
            v-for="(abbreviation, longName) in usStates"
            :value="abbreviation"
            :key="abbreviation">
            {{longName }}
          </option>
        </b-select>
      </b-field>

      <b-field label="City" class="column is-6" :type="calculateFieldType('city')">
        <b-input v-model="companyInfo.companyAddresses[0].companyAddressCity"
                 name="city"
                 v-validate="{required: true}"
                 required
                 :disabled="isFetchingAddress"></b-input>
      </b-field>
    </b-field>

    <b-field grouped class="columns">
      <b-field label="Tax ID (EIN)" :type="calculateFieldType('EIN')" :message="validationErrors.has('EIN')? 'This is not a valid EIN format' : ''"   class="column">
        <b-input v-model="companyInfo.companyTaxId"
                 name="EIN"
                 v-validate="{required: true, regex: /\d{2}-\d{7}/}"
                 required
                 @blur="validateEin" autofocus></b-input>
      </b-field>
      <b-field label="Dun & Bradstreet Number (DUNS)"  class="column">
        <b-input v-model="companyInfo.companyDunAndBradStreetNumber"
                 type="text"
                 placeholder="If you don't have one, leave blank"></b-input>
      </b-field>
    </b-field>


  </form>
</template>

<script>
import { mapState } from 'vuex'
import { debounce, isEmpty, merge } from 'lodash-es'

import AddressLookup from '@/api-interface/address-lookup'
import industryList from '@/lookup-data/industries'
import usStates from '@/lookup-data/us-states'
import PhoneNumberInput from '@/components/PhoneNumberInput'

const DEBOUCE_DELAY = 300
const validEinFormat = new RegExp(/^\d{2}-\d{7}$/)

export default {
  name: 'FormCompanyInformation',

  model: {
    prop: 'existingCompanyInfo',
    event: 'input'
  },

  props: {
    existingCompanyInfo: {
      type: Object,
      default: () => {}
    }
  },

  mounted() {
    if (!isEmpty(this.existingCompanyInfo)) {
      this.companyInfo = merge(this.companyInfo, this.existingCompanyInfo)

      if (this.companyInfo.companyPhones.length === 0) {
        this.companyInfo.companyPhones = [
          {
            companyPhoneDigits: null,
            companyPhonePrimaryFlag: true,
            companyPhoneType: 'office'
          }
        ]
      }
    }
  },

  data() {
    return {
      companyInfo: {
        companyName: '',
        companyIndustry: '',
        companyTaxId: '',
        companyPhones: [
          {
            companyPhoneDigits: null,
            companyPhonePrimaryFlag: true,
            companyPhoneType: 'office'
          }
        ],
        companyAddresses: [
          {
            companyDunAndBradStreetNumber: null,
            companyAddressType: 'N/A',
            companyAddressPrimaryFlag: true,
            companyAddressStreet1: '',
            companyAddressStreet2: '',
            companyAddressZip: '',
            companyAddressState: '',
            companyAddressCity: ''
          }
        ]
      },
      admin: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        title: ''
      },
      isFetchingAddress: false,
      isValidEin: true,
      usStates,
      userIsAdmin: false
    }
  },

  components: {
    PhoneNumberInput
  },

  computed: {
    ...mapState('user', { userInfo: 'currentUserInfo' }),
    /**
     * Industry list filtered based on the current value of "industry"
     * @returns {*}
     */
    filteredIndustryList() {
      const industryValue = this.companyInfo.companyIndustry.toLowerCase()
      return industryList.filter((industry) => industry.toLowerCase().includes(industryValue))
    }
  },

  methods: {
    setCurrentUserAsAdmin() {
      if (this.userIsAdmin) {
        this.admin.firstName = this.userInfo.accountFirstName
        this.admin.lastName = this.userInfo.accountLastName
        this.admin.email = this.userInfo.accountEmailAddress
        this.admin.phone = this.userInfo.accountPhones[0].accountPhoneDigits
      } else {
        this.admin.firstName = ''
        this.admin.lastName = ''
        this.admin.email = ''
        this.admin.phone = ''
      }
    },

    /**
     * Looks up an address based on the zip code. This is done using an external API.
     */
    lookupAddress: debounce(async function() {
      this.isFetchingAddress = true

      try {
        const response = await AddressLookup.searchByZip(this.companyInfo.companyAddresses[0].companyAddressZip)
        this.companyInfo.companyAddresses[0].companyAddressCity = response.address.city || ''
        this.companyInfo.companyAddresses[0].companyAddressState = response.address.state || ''
      } catch (error) {
        this.companyInfo.companyAddresses[0].companyAddressCity = ''
        this.companyInfo.companyAddresses[0].companyAddressState = ''
        console.error(`invalid zip code -- [${this.companyInfo.companyAddresses[0].companyAddressZip}]: `, error)
      } finally {
        this.isFetchingAddress = false
      }
    }, DEBOUCE_DELAY),

    /**
     * Does the EIN matches the valid EIN format
     * @returns {Boolean}
     */
    validateEin() {
      // this was intentionally not done as a computed property because we only want to perform the validation after
      // the user leaves the field
      this.isValidEin = validEinFormat.test(this.companyInfo.companyTaxId)
    },

    async validate() {
      return this.$validator.validate()
    },

    /**
     *
     * @param {String} fieldName The name of the field in question (this is the name given to vee-validate)
     * @returns {string} CSS class to be applied
     */
    calculateFieldType(fieldName) {
      // TODO: this might make a good candidate for a mix-in
      return this.validationErrors.has(fieldName) ? 'is-danger' : ''
    }
  },

  watch: {
    'companyInfo.companyTaxId'(newValue, oldValue) {
      if (newValue === oldValue) {
        // do nothing
        return
      }

      let formattedValue
      // strip any non-numeric characters from the incoming value
      const cleanOldValue = newValue
        .match(/\d+/g)
        .map(String)
        .join('')

      const first = cleanOldValue.substring(0, 2)
      const second = cleanOldValue.substring(2)

      if (cleanOldValue.length <= 2) {
        formattedValue = cleanOldValue
      } else if (second) {
        formattedValue = `${first}-${second}`
      } else if (first) {
        formattedValue = first
      }

      this.companyInfo.companyTaxId = formattedValue
    },
    // TODO: this might make a good candidate for a mix-in
    validationFields: {
      deep: true,
      async handler() {
        const isValid = this.$validator.errors.items.length === 0

        this.$emit('input', this.companyInfo)
        this.$emit('isComplete', isValid)
      }
    }
  }
}
</script>

<style scoped>
</style>
